import {
  IAccessibility,
  IAdditionalActivities,
  IGastro,
  IOvernightStay,
  IRecommendationReason,
  IRegion,
  IRouteType,
  ISex,
  ISuitability,
  ITerrain,
  ITripDuration,
  ITripSuggestionStatus,
} from 'src/graphql'
import { DefineLocaleMessage } from 'vue-i18n'

const translations: DefineLocaleMessage = {
  general: {
    language: {
      cs: 'Czech',
      en: 'English',
    },
    format: {
      datetime: 'd/M/yyyy H:mm',
      date: 'd/M/yyyy',
    },
    button: {
      cancel: 'Cancel',
      close: 'Close',
      back: 'Back',
      contactSupport: 'Contact support',
    },
    enum: {
      recommendationReason: {
        [IRecommendationReason.Nearby]: 'Near you',
        [IRecommendationReason.Similar]: 'Similar trip',
        [IRecommendationReason.SimilarToFavorites]: 'Similar to your favorites',
        [IRecommendationReason.MatchesPreferences]: 'Matches your preferences',
        [IRecommendationReason.Popular]: 'Popular',
        [IRecommendationReason.Fresh]: 'New',
        [IRecommendationReason.Curated]: 'Recommended',
      },
      tripLabel: {
        fresh: 'New',
      },
      tripSuggestionStatus: {
        [ITripSuggestionStatus.Reserved]: 'Reserved',
        [ITripSuggestionStatus.ContributionSubmitted]: 'Under review',
        [ITripSuggestionStatus.Converted]: 'Converted to trip',
      },
      tripDuration: {
        [ITripDuration.ShortWalk]: 'Short walk',
        [ITripDuration.HalfDay]: 'Half day',
        [ITripDuration.WholeDay]: 'Whole day',
        [ITripDuration.Weekend]: 'Weekend',
      },
      gastro: {
        [IGastro.FineCoffee]: 'Fine coffee',
        [IGastro.Bistro]: 'Bistro',
        [IGastro.Restaurant]: 'Restaurant',
        [IGastro.Confectionery]: 'Confectionery',
        [IGastro.ForestBar]: 'Forest bar',
      },
      suitability: {
        [ISuitability.Stroller]: 'Stroller',
        [ISuitability.Children]: 'Children',
        [ISuitability.Dogs]: 'Dogs',
      },
      additionalActivities: {
        [IAdditionalActivities.WellnessSauna]: 'Wellness/Sauna',
        [IAdditionalActivities.Swimming]: 'Swimming',
        [IAdditionalActivities.Playgrounds]: 'Playgrounds',
        [IAdditionalActivities.Castles]: 'Castles',
        [IAdditionalActivities.LookoutTowers]: 'Lookout towers',
        [IAdditionalActivities.Picnic]: 'Picnic',
        [IAdditionalActivities.EducationalTrails]: 'Educational trails',
      },
      overnightStay: {
        [IOvernightStay.UnderTheSky]: 'Under the sky',
        [IOvernightStay.Camping]: 'Camping',
        [IOvernightStay.Shelters]: 'Shelters',
        [IOvernightStay.Hostel]: 'Hostel',
        [IOvernightStay.Pension]: 'Pension',
        [IOvernightStay.Glamping]: 'Glamping',
        [IOvernightStay.Hotel]: 'Hotel',
      },
      routeType: {
        [IRouteType.Circuit]: 'Circuit',
        [IRouteType.AToB]: 'A to B',
      },
      accessibility: {
        [IAccessibility.TrainBus]: 'Train/Bus',
        [IAccessibility.PublicTransport]: 'PublicTransport',
      },
      tripCharacteristics: {
        [ISuitability.Children]: 'Activities and places suitable for children',
        [ISuitability.Dogs]: 'Dog parks or refreshments for dogs',
        [ISuitability.Stroller]: 'Stroller-friendly',
        [IGastro.Bistro]: 'Bistro refreshments',
        [IGastro.Confectionery]: 'Sweet treats at a confectionery',
        [IGastro.ForestBar]: 'Simple refreshment at a forest bar',
        [IGastro.FineCoffee]: 'Specialty coffee shop',
        [IGastro.Restaurant]: 'Restaurant dining',
        [IRouteType.AToB]: 'One-way route',
        [IRouteType.Circuit]: 'Circular route',
        [IAccessibility.TrainBus]: 'Train or bus accessible',
        [IAccessibility.PublicTransport]: 'Public transport accessible',
        [IOvernightStay.UnderTheSky]: 'Under the sky',
        [IOvernightStay.Camping]: 'Camping',
        [IOvernightStay.Shelters]: 'Shelter',
        [IOvernightStay.Hostel]: 'Hostel',
        [IOvernightStay.Pension]: 'Pension',
        [IOvernightStay.Glamping]: 'Glamping',
        [IOvernightStay.Hotel]: 'Hotel',
        [IAdditionalActivities.Playgrounds]: 'Playgrounds along the route',
        [IAdditionalActivities.WellnessSauna]: 'Wellness or sauna',
        [IAdditionalActivities.Picnic]: 'Picnic spot',
        [IAdditionalActivities.LookoutTowers]: 'Lookout towers and viewpoints',
        [IAdditionalActivities.Swimming]: 'Swimming spot',
        [IAdditionalActivities.Castles]: 'Castle, chateau or ruins',
        [IAdditionalActivities.EducationalTrails]: 'Educational trail',
        [ITerrain.Asphalt]: 'Paved asphalt road',
        [ITerrain.FieldPath]: 'Field path with varying difficulty',
        [ITerrain.Stone]: 'Rocky terrain',
        [ITerrain.Mixed]: 'Mixed terrain - see route description for details',
      },
      terrain: {
        [ITerrain.Asphalt]: 'Asphalt',
        [ITerrain.FieldPath]: 'Field path',
        [ITerrain.Stone]: 'Stone',
        [ITerrain.Mixed]: 'Mixed',
      },
      region: {
        [IRegion.CentralBohemian]: 'Central Bohemia',
        [IRegion.HradecKralove]: 'Hradec Kralove',
        [IRegion.Liberec]: 'Liberec',
        [IRegion.Olomouc]: 'Olomouc',
        [IRegion.Usti]: 'Usti nad Labem',
        [IRegion.KarlovyVary]: 'Karlovy Vary',
        [IRegion.MoravianSilesian]: 'Moravian-Silesian',
        [IRegion.Pardubice]: 'Pardubice',
        [IRegion.Pilsen]: 'Pilsen',
        [IRegion.PragueCapital]: 'Prague-Capital',
        [IRegion.SouthBohemian]: 'South Bohemia',
        [IRegion.SouthMoravian]: 'South Moravian',
        [IRegion.Vysocina]: 'Vysocina',
        [IRegion.Zlin]: 'Zlin',
      },
      sex: {
        [ISex.M]: 'Male',
        [ISex.F]: 'Female',
        unspecified: 'Unspecified',
      },
    },
    validations: {
      required: 'This field is required',
      phone: 'Invalid phone number',
      email: 'Invalid email address',
      url: 'Invalid URL',
      minLength: 'Minimum length is {minLength} characters',
      maxLength: 'Maximum length is {maxLength} characters',
      isPositive: 'Value must be positive number',
      isPositiveOrZero: 'Value must be positive number or zero',
      minValue: 'Minimum value is {min}',
      maxValue: 'Maximum value is {max}',
      mapsLink: 'Invalid Mapy.com link',
      matchingPasswords: 'Passwords do not match',
      passwordsDifferent: 'Passwords must be different',
    },
    error: {
      unexpectedError: 'Unexpected error occurred. Please try again later.',
      refreshTokenExpired: 'Your session has expired. Please Sign In again.',
    },
  },
  app: {
    pwa: {
      install: {
        title: 'Add <strong>rooute</strong> app to your home screen?',
        caption: {
          android:
            '<p class="q-my-sm">By adding, you will get easier access to rooute without launching a browser.</p>',
          ios:
            '<p class="q-my-sm">By adding, you will get easier access to rooute without launching a browser.</p>' +
            '<p class="q-my-sm">To add it, you need to open this page in Safari browser. In the <i class="fa-solid fa-arrow-up-from-square"></i> Share menu, select <strong>Add to Home Screen</strong>.</p>',
          notChrome:
            '<p class="q-my-sm">By adding, you will get easier access to rooute without launching a browser.</p>' +
            '<p class="q-my-sm">Your browser unfortunately does not support this feature, but you just need to open this page in Chrome browser.</p>',
        },
        confirm: 'Add',
      },
      updateAvailable: {
        message: 'A new version of the app is available!',
        caption:
          'Download the update to get the latest version and ensure everything works smoothly.',
        download: 'Download',
        later: 'Later',
      },
      applyUpdate: {
        message: 'Update downloaded',
        caption: 'Install it now - we fixed bugs and added new features.',
        install: 'Install',
      },
      offline: {
        message: 'Your internet connection was lost',
        caption: 'The app cannot perform at full capacity until it is restored.',
      },
      error: {
        message: 'Oops, something went wrong',
        caption:
          'Have you tried turning it off and on again? Try that. If the problem persists, contact us.',
      },
    },
    seo: {
      description: 'Find your next adventure!',
    },
  },
  layout: {
    public: {
      toWeb: 'Go to website',
    },
  },
  component: {
    publicHeader: {
      toApp: 'Open app',
    },
    publicFooter: {
      partnership: {
        title: "Let's collaborate",
        text: 'On creating trips or anything else. Contact us at',
      },
      follow: {
        title: 'Follow us',
      },
      important: {
        title: 'Important links',
        privacyPolicy: 'Privacy policy',
        termsOfService: 'Terms of service',
      },
      cookiePreferences: 'Cookie settings',
      createdBy: 'Created by',
    },
    mobileTabMenu: {
      dashboard: 'Wall',
      map: 'Map',
      trips: 'Trips',
      profile: 'Profile',
    },
    userMenu: {
      signOut: 'Sign out',
    },
    articlePreviewItem: {
      goToArticle: 'Go to article',
    },
    feedItem: {
      unpublished: 'Unpublished',
      readMore: 'Read more',
      shareText: 'Check out this article on rooute!',
      toast: {
        bookmarkError:
          'Oops, failed to add the article to bookmarks. Try again later or contact support if the problem persists.',
        unbookmarkError:
          'Oops, failed to remove the article from bookmarks. Try again later or contact support if the problem persists.',
      },
    },
    tripPreviewItem: {
      unpublished: 'Unpublished',
      toast: {
        bookmarkError:
          'Oops, failed to add the trip to favorites. Try again later or contact support if the problem persists.',
        unbookmarkError:
          'Oops, failed to remove the trip from favorites. Try again later or contact support if the problem persists.',
      },
    },
    tripRating: {
      yourRating: 'Your rating:',
      pending: 'Pending review',
      deletedUser: 'Deleted user',
    },
    tripRatingScore: {
      noRating: 'Not rated yet',
    },
    onboardingOverlay: {
      first: {
        title: 'Tell us a bit about yourself',
        city: {
          label: 'Where are you from?',
          placeholder: 'Enter your city',
          noResults:
            "We don't recognize this - is there a typo? If you can't find your city, contact our support and leave this field empty.",
        },
        sex: { label: 'Your sex?', placeholder: 'Choose your sex' },
        age: {
          label: 'Your age?',
          placeholder: 'Select your age range',
          '0': 'Less than 18',
          '18': '18 to 29',
          '30': '30 to 44',
          '45': '45 to 59',
          '60': '60+',
        },
        submit: 'Continue',
      },
      second: {
        title: 'Tell us about your interests',
        beverage: {
          label: 'Coffee or tea?',
          coffee: 'Coffee',
          tea: 'Tea',
        },
        sleeping: {
          label: 'Sleeping bag or hotel?',
          sleepingBag: 'Sleeping bag',
          hotel: 'Hotel',
        },
        transportation: {
          label: 'Public transport or car?',
          publicTransport: 'Public transport',
          car: 'Car',
        },
        travelCompanionPreference: {
          label: 'Alone or with a travel companion?',
          alone: 'Alone',
          withTravelCompanion: 'With companion',
        },
      },
      third: {
        title: ' What kind of trips do you like?',
        tripDurations: {
          label: 'What trip duration do you prefer?',
        },
        tripCharacteristics: {
          label: 'What do you like to do on the trip?',
          strollerFriendly: 'Stroller friendly',
          entertainmentForChildren: 'Entertainment for children',
          dogFriendly: 'Dog friendly',
          coffeeAndCake: 'Coffee and cake',
          gastro: 'Quality gastro',
          interestingActivities: 'Interesting activities on route',
        },
      },
      fourth: {
        title: 'Thank you very much for completing the questionnaire!',
        text: 'The questionnaire helps us better understand who uses our application and create content that interests you.',
        referralSource: {
          label: 'How did you learn about rooute?',
          instagram: 'Instagram',
          searchEngine: 'Search engine',
          recommendation: 'Recommendation',
          other: 'Other',
          cantRemember: "Can't remember",
          otherPlaceholder: 'Enter your referral source',
        },
        validations: {
          otherRequired: `Can't remember? Choose the "Can't remember" option.`,
        },
      },
      next: 'Next',
      previous: 'Previous',
      finish: 'Finish',
    },
    mapFilterControl: {
      label: 'Filter',
    },
  },
  dialog: {
    cookie: {
      title: 'Cookies for the trip? Choose what you prefer',
      description:
        'For rooute to work, we need to store some data in your browser (cookies). For the best app functionality, accept all cookies – or choose what you want to share. Either way, everything is anonymous and we protect your data.',
      necessary: {
        label: 'Essential data',
        info: "This is a must-have. Without this data, rooute simply doesn't work – it includes your login, language settings, and other basic functions.",
      },
      analytics: {
        label: 'Analytics data',
        info: "We're constantly improving rooute to bring you the best trips and articles. This data helps us know what to work on and what already works great. All anonymous, no Big Brother.",
      },
      marketing: {
        label: 'Marketing data',
        info: 'This data helps us understand how our marketing efforts work – and allows us to offer rooute to more adventure enthusiasts.',
      },
      diagnostics: {
        label: 'Diagnostic data',
        info: 'This data helps us quickly detect any issues and ensure rooute runs smoothly for you.',
      },
      acceptNecessary: 'Essential only',
      savePreferences: 'Save my choice',
      showDetails: 'Show details',
      acceptAll: 'Accept all',
      moreOn:
        'We promise not to share your data and handle it as securely as possible. Learn more in our',
      privacyPolicy: 'Privacy policy',
    },
    changeCredentials: {
      title: 'Change credentials',
      firstname: { label: 'Firstname', placeholder: 'Enter your firstname' },
      lastname: { label: 'Lastname', placeholder: 'Enter your lastname' },
      submit: 'Save credentials',
      toast: {
        success: 'Your credentials were successfully changed.',
        error:
          'There was an error changing your credentials. Try again later or contact the support.',
      },
    },
    changePassword: {
      title: 'Change password',
      oldPassword: { label: 'Current password', placeholder: 'Enter your current password' },
      newPassword: { label: 'New password', placeholder: 'Enter new secure password' },
      submit: 'Change password',
      toast: {
        success: 'Your password was successfully changed.',
        error: 'There was an error changing your password. Try again later or contact the support.',
      },
    },
    changeInterests: {
      title: 'Change interests',
      form: {
        gastro: 'Gastro',
        suitableFor: 'Suitable for:',
        additionalActivities: 'Additional activities',
        overnightStay: 'Overnight stay',
        validations: {
          maxLength: 'You can choose up to {length} interests',
        },
      },
      submit: 'Save interests',
      toast: {
        success: 'Your interests were successfully changed.',
        error:
          'There was an error changing your interests. Try again later or contact the support.',
      },
    },
    changeLanguage: {
      title: 'Change language',
    },
    changeLocation: {
      title: 'Change your location',
      region: 'region',
      location: { label: 'Location', placeholder: 'Enter your location' },
      submit: 'Save location',
      toast: {
        success: 'Your location was successfully changed.',
        error: 'There was an error changing your location. Try again later or contact the support.',
      },
    },
    tripFilter: {
      title: 'Find trips tailored for you',
      form: {
        duration: 'Duration',
        distance: 'Trip length',
        type: 'Type',
        terrain: 'Terrain',
        area: 'Location',
        suitability: 'Suitable for:',
        accessibility: 'Accessibility:',
        gastro: 'Gastro:',
        additionalActivities: 'Additional activities',
        overnightStay: 'Overnight options',
      },
      submit: 'Show trips ({count})',
      noResults: 'No results',
      reset: 'Reset filters',
      mapDisplayMode: {
        label: 'Show on map',
        all: 'All',
        trips: 'Trips',
        suggestions: 'Suggestions',
      },
    },
    tripSuitabilityInfo: {
      title: 'Route suitability',
      [ISuitability.Stroller]: {
        title: 'Stroller friendly',
        description:
          'Trail suitable for strollers has a moderately firm surface without significant obstacles, stairs or steep climbs, allowing comfortable and smooth movement.',
      },
      [ISuitability.Children]: {
        title: 'Children friendly',
        description:
          'Trail suitable for children is safe, short and easy with options for entertainment or rest.',
      },
      [ISuitability.Dogs]: {
        title: 'Dog friendly',
        description:
          'Trail suitable for dogs is safe, calm and spacious, with minimal traffic and opportunities for free movement.',
      },
    },
    tripRating: {
      publicReview: 'Public review',
      text: { label: 'Comment', placeholder: 'Enter your comment' },
      submit: 'Rate',
      toast: {
        success: 'Your rating was submitted and is being reviewed.',
        error: 'There was an error adding your rating. Try again later or contact the support.',
        banned:
          'You are currently unable to submit reviews. Contact support if you believe this is an error.',
      },
      validations: {
        noRating: 'You must add at least one star',
      },
    },
    tripCharacteristics: {
      title: 'What to expect on the trip',
    },
    locationRewardDetail: {
      toSwipe: 'Swipe to select reward',
      isSwiped: 'Reward selected!',
    },
  },
  page: {
    notFound: {
      title: 'Page not found',
      text: "We couldn't find the page you were looking for.",
      backToHome: 'Back to home',
    },
    landing: {
      title: 'Welcome to rooute!',
      description: 'Find your next adventure!',
      newsletter: {
        success: 'Thank you for subscribing to our newsletter.',
        error:
          'There was an error subscribing to our newsletter. Try again later or contact the support.',
      },
    },
    blog: {
      title: 'Blog',
      description: 'Tips for your next adventure!',
      loadMore: 'Load more',
      noMoreArticles: "That's it!\nNew stories appear here every week, so come back soon.",
      noMoreArticles_notAuthenticated:
        "That's it!\nNo more articles here, but you'll find plenty more stories on our Dashboard. Just sign in.",
      signIn: 'Sign in',
    },
    termsOfService: {
      title: 'Terms of service',
    },
    privacyPolicy: {
      title: 'Privacy policy',
    },
    signpost: {
      title: 'Hi, welcome to rooute!',
      text1: "If you're already part of our adventure community, sign in and see what's new.",
      text2:
        "And if you're just getting started with new adventures, register first so you can choose where to go next.",
      signIn: 'Sign in',
      signUp: "Don't have an account? Sign up",
    },
    signIn: {
      title: 'Sign In',
      email: { label: 'Email', placeholder: 'Enter your email' },
      password: { label: 'Password', placeholder: 'Enter your password' },
      forgottenPassword: 'Forgot password?',
      submit: 'Sign in',
      toast: {
        invalidCredentials: 'Invalid email or password.',
        invalidRecaptcha:
          'We could not verify your identity. Please try again. If the error occurs again try to disable extensions like AdBlock or contact the support.',
      },
    },
    signUp: {
      title: 'Sign Up',
      email: { label: 'Email', placeholder: 'Enter your email' },
      submit: 'Send confirmation email',
      submitted: 'Confirmation email was sent to your email.',
      toast: {
        error: 'There was an error sending confirmation email.',
      },
    },
    finishSignUp: {
      title: 'Finish registration',
      firstname: { label: 'First name', placeholder: 'Enter your first name' },
      lastname: { label: 'Last name', placeholder: 'Enter your last name' },
      password: { label: 'Password', placeholder: 'Enter secure password' },
      passwordConfirmation: { label: 'Confirm password', placeholder: 'Repeat password' },
      language: { label: 'Language' },
      submit: 'Finish registration',
      toast: {
        success: 'Your account was successfully created. You can Sign In now.',
        error: 'There was an error creating your account.',
      },
    },
    forgottenPassword: {
      title: 'Forgotten password',
      email: { label: 'Email', placeholder: 'Enter your email' },
      submitted: 'Reset instructions were sent to your email.',
      submit: 'Request reset',
      back: 'Back to Sign In',
      toast: {
        error: 'There was an error requesting reset.',
      },
    },
    resetPassword: {
      title: 'Reset password',
      password: { label: 'Password', placeholder: 'Enter secure password' },
      passwordConfirmation: { label: 'Confirm password', placeholder: 'Repeat password' },
      submit: 'Reset password',
      toast: {
        success: 'Your password was successfully reset. You can Sign In with your new password.',
        error: 'There was an error resetting your password.',
      },
    },
    dashboard: {
      title: 'News',
      latest: 'Latest articles',
      noArticlesFound: 'We could not find any articles for you.',
    },
    articleDetail: {
      featuredArticles: 'Featured articles:',
      notAvailable: 'This article is only available in Czech',
      switchLanguage: 'View in Czech',
      backToBlog: 'Back to blog',
      confirmBookmark: {
        title: 'Save article',
        description: 'Do you want to save this article to your bookmarks?',
        confirm: 'Save',
      },
    },
    map: {
      title: 'Map',
      layer: {
        outdoor: 'Outdoor',
        basic: 'Basic',
        winter: 'Winter',
        aerial: 'Aerial',
      },
      popup: {
        here: {
          text: 'You are here',
        },
        goToTrip: 'Go to trip',
        suggestion: {
          available: 'Available',
          reserved: 'Reserved',
          viewDetail: 'View detail',
        },
      },
    },
    trips: {
      title: 'Trips',
      recommended: 'Recommended',
      noTripsFound: 'We could not find any trips for you.',
      header: {
        sort: {
          publishedAt: 'Most recent',
          finishedCount: 'Most visited',
          averageRating: 'Best rating',
        },
      },
    },
    tripDetail: {
      characteristics: 'Characteristics:',
      tripDescription: 'Route description:',
      partnerLocations: 'Verified places on the route:',
      rating: {
        title: 'Ratings:',
        noRatings: 'No ratings yet. Be the first to rate this trip!',
        showAll: 'Show all ratings',
      },
      rate: 'Rate',
      similarTrips: 'You might also like',
      markFinished: 'Mark as finished',
      removeFinished: 'Unmark as finished',
      support: "Route didn't match the description? Let us know",
      toast: {
        toggledFinished:
          'Great! You will find the trip added to your profile in the Finished trips section',
      },
      confirmFavorite: {
        title: 'Add to favorites',
        description: 'Do you want to add this trip to your favorites?',
        confirm: 'Add to favorites',
      },
      confirmFinished: {
        title: 'Mark as finished',
        description: 'Do you want to mark this trip as finished?',
        confirm: 'Mark as finished',
      },
      notAvailable: 'This trip is only available in Czech',
      switchLanguage: 'View in Czech',
      backToTrips: 'Back to trips',
    },
    partnerLocationDetail: {
      rewards: 'Rewards at this location:',
    },
    profile: {
      title: 'Profile',
      header: {
        locationUnknown: 'Earth',
      },
      tiles: {
        totalKm: {
          title: 'Total walked',
        },
        rewards: {
          title: 'Rewards',
        },
        tripCount: {
          title: 'Finished trips',
        },
      },
      bookmarkedTrips: 'Favorite trips ({val})',
      bookmarkedArticles: 'Bookmarked articles ({val})',
      finished: 'Finished trips ({val})',
      tripSuggestions: 'My trip suggestions',
      rewards: 'Collected rewards ({val})',
    },
    settings: {
      title: 'Settings',
      credentials: 'My credentials',
      password: 'Password',
      subscription: 'Subscription',
      interests: { title: 'Interests', none: 'None' },
      location: 'Location',
      language: 'Language',
      support: 'Contact support',
      deleteAccount: 'Delete account',
      privacyPolicy: 'Privacy policy',
      termsOfService: 'Terms of service',
      about: 'About',
      share: {
        label: 'Share with friends',
        shareApp: {
          title: 'Try Rooute app',
          text: 'Spent quality time outside and leave the planning on us!',
        },
      },
      signOut: 'Sign out',
      version: 'Version: {version}',
    },
    bookmarkedArticles: {
      title: 'Bookmarked articles',
      notFound: 'No bookmarked articles yet. Check the dashboard and save something for later.',
    },
    finishedTrips: {
      title: 'Finished trips',
      notFound:
        "Tick tock - isn't it time to head outside? Put on your shoes, complete a trip and don't forget to mark it so you can see everything you've done.",
    },
    favoriteTrips: {
      title: 'Favorite trips',
      notFound:
        'Dream big! Select the trips you want to complete and save them so you can easily find them when the time comes.',
    },
    tripSuggestionDetail: {
      by: 'By:',
      description: 'Description',
      viewRoute: 'View route',
      reserveSuccess: 'Trip suggestion reserved successfully!',
      reserveError: 'Failed to reserve trip suggestion.',
      cancelSuccess: 'Reservation cancelled.',
      cta: {
        availableTitle: 'Want to walk this trip?',
        availableDescription:
          'Reserve this suggestion and you will have {days} days to walk the route, take photos, and write it up.',
        reserve: 'Reserve',
        reservedByMeTitle: "It's reserved for you!",
        daysRemaining: '{days} days remaining to complete.',
        startContributing: 'Start contributing',
        cancelReservation: 'Cancel reservation',
        underReviewTitle: 'Your contribution is under review',
        underReviewDescription:
          'Thanks for submitting! Our editors will review your contribution soon.',
        reservedByOtherTitle: 'Currently reserved',
        reservedByOtherDescription:
          'This suggestion is currently reserved by another user. Try again later.',
        multipleTitle: 'Share your experience on this route',
        multipleDescription:
          'Anyone can contribute to this trip suggestion. Walk the route, take photos, and share your story.',
        contribute: 'Contribute',
        alreadyContributed: "You've already contributed",
      },
    },
    tripSuggestionContribute: {
      title: 'Contribute to trip',
      daysRemaining: '{days} days remaining',
      contentLabel: 'Trip description - your experience on the route',
      suggestionDescription: 'What we need from you:',
      routeLinkLabel: 'Route link (mapy.cz)',
      routeLinkRequired: 'Route link is required',
      imagesLabel: 'Trip photos',
      imagesHint:
        'Quality photos help other travellers discover amazing routes. Please use high-resolution images (min. 1500px wide, max 10 MB).',
      imagesRequired: 'Add at least 5 photos',
      imageUploadError: 'Failed to upload image',
      imageFileTooLarge: 'File is too large (max 10 MB)',
      imageInvalidWidth:
        'This image is only {width}px wide. We need at least {minWidth}px so it looks great for everyone.',
      submit: 'Submit contribution',
      confirmTitle: 'Submit contribution?',
      confirmMessage: 'Your contribution will be sent to editors for review. Continue?',
      success: 'Contribution submitted successfully!',
    },
    myTripSuggestions: {
      title: 'My trip suggestions',
      tabReserved: 'Reserved',
      tabContributed: 'Contributed',
      empty: 'No trip suggestions yet',
      emptyDescription: 'Check the map and reserve a trip suggestion that interests you.',
      emptyContributed: 'No contributions yet',
      emptyContributedDescription: 'Find a trip suggestion and share your experience.',
      daysRemaining: '{days} days remaining',
    },
    deleteAccount: {
      seoTitle: 'Delete account',
      title: 'Every trip comes to an end...',
      subtitle: `... but sometimes it's just a poorly marked trail or the sun behind clouds that spoils your hike.`,
      text: `If there's something about rooute that doesn't work for you, leave us a message - maybe we can remove the obstacle on the trail before our journey together really ends.`,
      feedback: {
        text: "Write to us and we'll do our best to make your hiking experience with us as great as possible.",
        sendFeedback: 'Send feedback',
        placeholder: 'Leave a message here, share your experience or idea what we can improve:',
        submit: 'Send',
      },
      deletion: {
        info: 'If your hiking with rooute really ends, we will delete your account and with it all saved trips, articles and settings. Your reviews will remain on the map, but without your name - as an anonymous trail for other travelers.',
        question: 'Do you really want to end our journey together?',
        delete: 'Yes, delete my rooute account',
        stay: `You convinced me, I'll stay`,
      },
      toast: {
        title: 'Three cheers! Thanks for giving rooute another chance.',
        text: `If you have an idea how to improve rooute or if something is not working, write to us at <a class="text-dark text-bold" href="mailto:porpora@rooute.app">porpora@rooute.app</a> - we'll do our best to make your hiking experience great.`,
        text2: 'And now back on the trail!',
        backToApp: 'Check the app and choose a new trip',
      },
    },
  },
}

export default translations
